import { Modal } from '@/components/ui/Modal';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { useState } from 'react';
import { CancelButton } from '@/components/ui/CancelButton';

/**
 * Confirmation Modal Props
 */
export interface ConfirmationModalProps {
  /** Modal open state */
  open: boolean;
  /** Callback when modal open state changes */
  onOpenChange: (open: boolean) => void;
  /** Modal title */
  title: string;
  /** Modal description */
  description: string;
  /** Confirm button text */
  confirmText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Modal variant (affects icon and button colors) */
  variant?: 'danger' | 'warning' | 'info';
  /** Callback when user confirms */
  onConfirm: () => void | Promise<void>;
  /** Loading state (disables buttons, shows spinner) */
  isLoading?: boolean;
  /** Render Cancel as a labelled secondary text button instead of the icon-only close button */
  textCancel?: boolean;
}

/**
 * Variant styling configuration
 */
const variantStyles = {
  danger: {
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    confirmBg: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    icon: ICONS.TRASH,
  },
  warning: {
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    confirmBg: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
    icon: ICONS.WARNING,
  },
  info: {
    iconBg: 'bg-soft-mint',
    iconColor: 'text-dark-teal',
    confirmBg: 'bg-primary-500 hover:bg-primary-600 focus:ring-primary-500',
    icon: ICONS.INFO,
  },
};

/**
 * ConfirmationModal Component
 *
 * A branded confirmation modal to replace browser `confirm()` dialogs.
 * Provides a better UX with loading states and customizable variants.
 *
 * @example
 * ```tsx
 * const [showConfirm, setShowConfirm] = useState(false);
 * const [isDeleting, setIsDeleting] = useState(false);
 *
 * const handleDelete = async () => {
 *   setIsDeleting(true);
 *   try {
 *     await deletePost(postId);
 *     setShowConfirm(false);
 *   } finally {
 *     setIsDeleting(false);
 *   }
 * };
 *
 * <ConfirmationModal
 *   open={showConfirm}
 *   onOpenChange={setShowConfirm}
 *   title="Delete Post"
 *   description="Are you sure you want to delete this post? This action cannot be undone."
 *   variant="danger"
 *   confirmText="Delete"
 *   cancelText="Cancel"
 *   onConfirm={handleDelete}
 *   isLoading={isDeleting}
 * />
 * ```
 */
export const ConfirmationModal = ({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'info',
  onConfirm,
  isLoading = false,
  textCancel = false,
}: ConfirmationModalProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const styles = variantStyles[variant];

  /**
   * Handle confirm button click
   * Supports both sync and async onConfirm handlers
   */
  const handleConfirm = async () => {
    try {
      setIsProcessing(true);
      await onConfirm();
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Handle cancel button click
   * Closes modal if not loading
   */
  const handleCancel = () => {
    if (!isLoading && !isProcessing) {
      onOpenChange(false);
    }
  };

  /**
   * Handle keyboard events
   * Escape: close modal (if not loading)
   * Enter: confirm (if not loading)
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isLoading || isProcessing) return;

    if (event.key === 'Escape') {
      handleCancel();
    } else if (event.key === 'Enter') {
      handleConfirm();
    }
  };

  const disabled = isLoading || isProcessing;

  return (
    <Modal
      open={open}
      onOpenChange={(newOpen) => {
        // Prevent closing during loading
        if (!newOpen && disabled) return;
        onOpenChange(newOpen);
      }}
      size="sm"
      showCloseButton={!disabled}
      raw
    >
      <div
        className="flex flex-col items-center text-center p-6"
        onKeyDown={handleKeyDown}
      >
        {/* Icon */}
        <div
          className={cn(
            'w-12 h-12 rounded-full flex items-center justify-center mb-4',
            styles.iconBg
          )}
        >
          <Icon
            name={styles.icon}
            size="lg"
            color={styles.iconColor}
            ariaHidden
          />
        </div>

        {/* Title */}
        <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2">{title}</h2>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-6">{description}</p>

        {/* Action Buttons */}
        <div className="flex justify-end gap-1.5 sm:gap-2 w-full">
          {/* Cancel Button */}
          {textCancel ? (
            <button
              type="button"
              onClick={handleCancel}
              disabled={disabled}
              className={cn(
                'px-4 h-10 text-sm rounded-lg font-medium',
                'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
                'focus:outline-none focus-visible:border-gray-500',
                'transition-colors duration-200',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {cancelText}
            </button>
          ) : (
            <CancelButton
              onClick={handleCancel}
              disabled={disabled}
              tooltip={cancelText}
            />
          )}

          {/* Confirm Button */}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={disabled}
            className={cn(
              'px-4 h-8 sm:h-10 text-sm rounded-lg font-medium',
              'text-white',
              styles.confirmBg,
              'focus:outline-none focus:ring-2 focus:ring-offset-2',
              'transition-colors duration-200',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'flex items-center justify-center gap-2'
            )}
          >
            {disabled && (
              <Icon
                name={ICONS.LOADER}
                size="sm"
                className="animate-spin"
                ariaHidden
              />
            )}
            {confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};
